
import React from 'react';
import { UseFormReturn, Path } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2 } from 'lucide-react';
import { CompetitionMatch, CompetitionDivision } from './types';
import { TrainingFormValues } from '../../types';

interface CompetitionMatchFormProps {
  form: UseFormReturn<TrainingFormValues>;
  index: number;
  onRemove: () => void;
}

const DIVISION_OPTIONS: { value: CompetitionDivision; label: string }[] = [
  { value: 'Child', label: 'Child (<16 yrs old)' },
  { value: 'Juvenile 1', label: 'Juvenile 1 (16-17 yrs old)' },
  { value: 'Juvenile 2', label: 'Juvenile 2 (17-18 yrs old)' },
  { value: 'Adult', label: 'Adult (18-29 yrs old)' },
  { value: 'Absolute', label: 'Absolute' },
  { value: 'Masters 1', label: 'Masters 1 (30-35 yrs old)' },
  { value: 'Masters 2', label: 'Masters 2 (36-40 yrs old)' },
  { value: 'Masters 3', label: 'Masters 3 (41-45 yrs old)' },
  { value: 'Masters 4', label: 'Masters 4 (46-50 yrs old)' },
  { value: 'Masters 5', label: 'Masters 5 (51-55 yrs old)' },
  { value: 'Masters 6', label: 'Masters 6 (56+ yrs old)' },
  { value: 'Other', label: 'Other' },
];

export const CompetitionMatchForm: React.FC<CompetitionMatchFormProps> = ({
  form,
  index,
  onRemove,
}) => {
  const match = form.watch(`competition_matches.${index}`) as CompetitionMatch;

  return (
    <div className="border rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-medium">Match {index + 1}</h4>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Gi/No-Gi</Label>
          <Select
            value={match?.gi_nogi || ''}
            onValueChange={(value) =>
              form.setValue(`competition_matches.${index}.gi_nogi`, value as 'gi' | 'nogi')
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="gi">Gi</SelectItem>
              <SelectItem value="nogi">No-Gi</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Division</Label>
          <Select
            value={match?.division || ''}
            onValueChange={(value) =>
              form.setValue(`competition_matches.${index}.division` as Path<TrainingFormValues>, value as CompetitionDivision)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select division..." />
            </SelectTrigger>
            <SelectContent>
              {DIVISION_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Weight Class</Label>
          <Input
            {...form.register(`competition_matches.${index}.weight_class`)}
            placeholder="e.g., -70kg"
          />
        </div>

        <div>
          <Label>Result</Label>
          <Select
            value={match?.result || ''}
            onValueChange={(value) =>
              form.setValue(`competition_matches.${index}.result`, value as 'win' | 'loss')
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="win">Win</SelectItem>
              <SelectItem value="loss">Loss</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Method</Label>
          <Select
            value={match?.method || ''}
            onValueChange={(value) =>
              form.setValue(`competition_matches.${index}.method`, value as 'points' | 'submission' | 'dq')
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="points">Points</SelectItem>
              <SelectItem value="submission">Submission</SelectItem>
              <SelectItem value="dq">DQ</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Video URL (Optional)</Label>
        <Input
          {...form.register(`competition_matches.${index}.video_url`)}
          placeholder="YouTube URL"
        />
      </div>
    </div>
  );
};
